/**
 * How a shul spells the Hebrew words it reads in English — the parsha, a Yom
 * Tov, a Hebrew month, and every word the app writes about Shabbos.
 *
 * A shul's own spelling is followed rather than normalized (the glossary's
 * writing rule). Two sources answer to this one setting:
 *
 * - **Names the calendar supplies** come from the calendar library's own
 *   locale, chosen by the setting in the API. No table of those here.
 * - **Words the app writes itself** — "Erev Shabbos", "Chol Hamoed", a flag's
 *   label, a picker's months — are authored once, in the Ashkenazi spelling,
 *   and `spelled` carries them into the shul's. These are not the library's
 *   words, so the library cannot spell them; where it has the same word, the
 *   API's tests hold this table to its answer.
 *
 * - `ashkenazi` — Parshas Pekudei, Erev Shabbos, Teves.
 * - `sephardic` — Parashat Pekudei, Erev Shabbat, Tevet: the library's default
 *   English, which it calls Sephardic transliteration.
 *
 * Ashkenazi is the default because it is how the congregations this platform
 * serves write their own sheets. A shul's own labels are its data and never
 * pass through here.
 */
export const CALENDAR_SPELLINGS = ['ashkenazi', 'sephardic'] as const

export type CalendarSpelling = (typeof CALENDAR_SPELLINGS)[number]

export const DEFAULT_CALENDAR_SPELLING: CalendarSpelling = 'ashkenazi'

/**
 * What a settings picker offers. Each description shows words a shul actually
 * sees change — the parsha, a day heading, the month in an English Hebrew date.
 */
export const CALENDAR_SPELLING_OPTIONS: {
  value: CalendarSpelling
  label: string
  description: string
}[] = [
  {
    value: 'ashkenazi',
    label: 'Ashkenazi',
    description: 'Parshas Pekudei · Erev Shabbos · 10th of Teves',
  },
  {
    value: 'sephardic',
    label: 'Sephardic',
    description: 'Parashat Pekudei · Erev Shabbat · 10th of Tevet',
  },
]

/**
 * The app's own words, authored in Ashkenazi, as the Sephardic spelling writes
 * them. Longest phrase first, so `Kabbalas Shabbos` is taken whole rather than
 * leaving `Kabbalas Shabbat`.
 *
 * `Motzei` and `Erev` are absent because both spellings write them the same —
 * the calendar library spells `Motzei Shabbat` and `Erev Shabbat`.
 */
const SEPHARDIC_WORDS: readonly [RegExp, string][] = [
  [/\bKabbalas Shabbos\b/g, 'Kabbalat Shabbat'],
  [/\bShabbosim\b/g, 'Shabbatot'],
  [/\bShabbos\b/g, 'Shabbat'],
  [/\bParshas\b/g, 'Parashat'],
  [/\bMevorchim\b/g, 'Mevarchim'],
  [/\bChol Hamoed\b/g, 'Chol HaMoed'],
  [/\bTeves\b/g, 'Tevet'],
]

/**
 * A phrase the app wrote, in the shul's spelling.
 *
 * Only for text the app authored. A shul's own label passed through here would
 * be respelled against its will, and a name the calendar library already
 * rendered is spelled by the library.
 */
export function spelled(text: string, spelling: CalendarSpelling): string {
  if (spelling === 'ashkenazi') return text
  return SEPHARDIC_WORDS.reduce((t, [from, to]) => t.replace(from, to), text)
}

/**
 * The Hebrew months as the calendar library names them in each spelling,
 * indexed by its month number. The two spellings differ only at Teves; `Adar`
 * is `Adar I` in a leap year.
 *
 * **The numbering counts from Nisan**, not from Tishrei: Nisan = 1 … Elul = 6 …
 * Tishrei = 7 … Adar = 12, Adar II = 13. Both orderings are real — the year
 * turns at Tishrei while the months are counted from Nisan — so a list written
 * from memory has an even chance of being the other one, and the mistake is
 * silent: a picker offering month names saves a number, and the wrong number
 * is a campaign that opens six months off.
 *
 * Kept here because the web app shows these without loading the library. The
 * API's tests hold this list to the library's own rendering, so the two cannot
 * drift apart.
 */
const MONTHS: Record<CalendarSpelling, readonly string[]> = {
  ashkenazi: [
    '',
    'Nisan',
    'Iyyar',
    'Sivan',
    'Tamuz',
    'Av',
    'Elul',
    'Tishrei',
    'Cheshvan',
    'Kislev',
    'Teves',
    'Sh’vat',
    'Adar',
    'Adar II',
  ],
  sephardic: [
    '',
    'Nisan',
    'Iyyar',
    'Sivan',
    'Tamuz',
    'Av',
    'Elul',
    'Tishrei',
    'Cheshvan',
    'Kislev',
    'Tevet',
    'Sh’vat',
    'Adar',
    'Adar II',
  ],
}

/**
 * A Hebrew month's English name in the shul's spelling. `leap` names month 12
 * `Adar I`, as a leap year does.
 */
export function hebrewMonthName(
  month: number,
  spelling: CalendarSpelling,
  leap = false,
): string {
  if (month === 12 && leap) return 'Adar I'
  return MONTHS[spelling][month] ?? ''
}

/**
 * The months a picker offers, in the library's numbering above, in the shul's
 * spelling — `{ value: '12', label: 'Adar' }`, the shape every picker takes. `adarII` adds month 13 for
 * a date that must be able to name the second Adar.
 */
export function hebrewMonthOptions(
  spelling: CalendarSpelling,
  opts: { adarII?: boolean } = {},
): { value: string; label: string }[] {
  const last = opts.adarII ? 13 : 12
  return Array.from({ length: last }, (_, i) => ({
    value: String(i + 1),
    label: hebrewMonthName(i + 1, spelling),
  }))
}
